import type {MouseEvent, ReactElement} from 'react';
import {useEffect} from 'react';
import React, {useState, useRef} from 'react';
import AiVoicePromptFilterPopup from '@Panels/ai-voice-panel/components/ai-voice-prompt-filter-popup';
import type {PromptFilterId, PromptSubfilter} from '@Panels/ai-voice-panel/ai-voice-panel.types';

interface AiVoicePromptFilterPopupWrapperProps {
  subfilters: PromptSubfilter[];
  onSelected(id: number, event: MouseEvent): void;
  id: PromptFilterId;
  initialSelection?: number;
}

export function AiVoicePromptFilterPopupWrapper({...props}: AiVoicePromptFilterPopupWrapperProps): ReactElement {
  const [selectedFilter, setSelectedFilter] = useState<number | undefined>(undefined);
  const [previewVoice, setPreviewVoice] = useState<string | undefined>(undefined);
  const [previewVoiceToLoad, setPreviewVoiceTrackToLoad] = useState<string | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onFilterClick = (id: number, event: MouseEvent): void => {
    setSelectedFilter(id);
    props.onSelected(id, event);
  };

  const onPause = (): void => {
    setPreviewVoice(undefined);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const onPlay = (track: string): void => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setPreviewVoiceTrackToLoad(track);
    setPreviewVoice(track);

    const audio = new Audio(track);
    audio.addEventListener('ended', onPause);

    audio
      .play()
      .then(() => {
        setPreviewVoiceTrackToLoad(undefined);
      })
      .catch((e: unknown) => {
        console.log(e);
      });

    audioRef.current = audio;
  };

  useEffect(() => {
    if (props.initialSelection) {
      setSelectedFilter(props.initialSelection);
    }

    return (): void => {
      onPause();
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', onPause);
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <AiVoicePromptFilterPopup
      key={props.id}
      subfilters={props.subfilters}
      onFilterClicked={onFilterClick}
      selectedSubfilter={selectedFilter}
      currentTrack={previewVoice}
      loadingTrack={previewVoiceToLoad}
      onTrackPlay={onPlay}
      onTrackPause={onPause}
    />
  );
}
